import Results from "client/common/components/results/Results";
import { ProductListSource } from "client/analytics/types";
import ProductCardList from "client/products/components/ProductCardList";
import ProductCardListHeader from "client/products/components/ProductCardListHeader";
import FilterLayout from "client/products/components/FilterLayout";
import FilterPanel from "client/products/components/FilterPanel";
import { PRODUCT_FILTER_DEFINITIONS } from "client/products/constants/filterDefinitions";
import { useFilterPanel } from "client/products/hooks";
import { ProductSearchFilters, SelectedProductFilters } from "client/products/types";

import { useCollectionSearch } from "../hooks";
import { StorefrontCollection } from "../types";

import styles from "../styles/CollectionResults.module.less";

type CollectionResultsProps = {
  collection: StorefrontCollection;
  /** Which surface is reporting the list. */
  listSource: ProductListSource;
};

const CollectionResults = ({ collection, listSource }: CollectionResultsProps) => {
  const { products, isLoading, isRefetching, error, selectedFilters, applyFilters, goToPage } =
    useCollectionSearch(collection);

  const panel = useFilterPanel<SelectedProductFilters, ProductSearchFilters>({
    definitions: PRODUCT_FILTER_DEFINITIONS,
    response: products,
    filters: selectedFilters,
    onChange: applyFilters,
    isEnabled: collection.show_filters,
  });
  const { filterOptions, showFilterPanel, hasSelectedFilters } = panel;

  if (isLoading) {
    return (
      <div className={styles.results}>
        <Results.Loading description="Loading products" />
      </div>
    );
  }
  if (error || !products) {
    return (
      <div className={styles.results}>
        <Results.FetchError error={error} entityName="product" />
      </div>
    );
  }

  return (
    <div className={styles.results}>
      <FilterLayout panel={showFilterPanel && <FilterPanel.Rail {...panel} />}>
        <div className={styles.list}>
          <ProductCardListHeader
            paginator={products.paginator}
            isRefetching={isRefetching}
            entityName="result"
            filterOptions={filterOptions}
            filters={selectedFilters}
            onChange={applyFilters}
            showFilters={collection.show_filters && !showFilterPanel}
          />
          <FilterPanel.AppliedRow {...panel} />
          {products.results.length ? (
            <ProductCardList
              results={products.results}
              listSource={listSource}
              paginator={products.paginator}
              listContext={{
                collectionSlug: collection.slug,
                numResults: products.paginator.count,
              }}
              onPageChange={goToPage}
              entityName="result"
              isNarrow={showFilterPanel}
            />
          ) : (
            <Results.None
              header="No products found"
              description={hasSelectedFilters ? "Try removing a filter." : undefined}
            />
          )}
        </div>
      </FilterLayout>
    </div>
  );
};

export default CollectionResults;
